// Options shown in the "Create Your Profile Card" modal, in display order.
// Each key is a backend PlayerCardOption name. `fields` (profile items) or
// `section` + `prop` (measurements and drills) say where the option's data
// lives in the PlayerCardDataVM card data.
export const OPTION_GROUPS = [
  {
    title: "Profile Information",
    hasScore: false,
    options: [
      {
        key: "ProfilePhoto",
        label: "Profile Photo",
        fields: ["profilePhotoUrl"],
      },
      { key: "Position", label: "Position", fields: ["position"] },
      { key: "PositionRank", label: "Position Rank", fields: ["positionRank"] },
      {
        key: "PositionScore",
        label: "Position Score",
        fields: ["positionScore"],
      },
      // The college card shows no graduation year.
      {
        key: "SchoolLocation",
        label: "School / Location",
        fields: ["school", "state"],
      },
    ],
  },
  {
    title: "Measurements",
    hasScore: true,
    options: [
      {
        key: "Weight",
        label: "Weight",
        section: "measurements",
        prop: "weight",
      },
      {
        key: "Height",
        label: "Height",
        section: "measurements",
        prop: "height",
      },
      {
        key: "Wingspan",
        label: "Wingspan",
        section: "measurements",
        prop: "wingspan",
      },
      { key: "Hand", label: "Hand", section: "measurements", prop: "hand" },
      { key: "Foot", label: "Foot", section: "measurements", prop: "foot" },
    ],
  },
  {
    title: "Athletic Drills",
    hasScore: true,
    options: [
      {
        key: "TenYardSplit",
        label: "10 Yard Split",
        section: "drills",
        prop: "tenYardSplit",
      },
      {
        key: "TwentyYardSplit",
        label: "20 Yard Split",
        section: "drills",
        prop: "twentyYardSplit",
      },
      {
        key: "FortyYardDash",
        label: "40 Yard Dash",
        section: "drills",
        prop: "fortyYardDash",
      },
      {
        key: "TwentyYardShuttle",
        label: "20 Yard Shuttle",
        section: "drills",
        prop: "twentyYardShuttle",
      },
      {
        key: "BenchPress",
        label: "Bench Press",
        section: "drills",
        prop: "benchPress",
      },
      {
        key: "VerticalJump",
        label: "Vertical Jump",
        section: "drills",
        prop: "verticalJump",
      },
      {
        key: "BroadJump",
        label: "Broad Jump",
        section: "drills",
        prop: "broadJump",
      },
      { key: "LDrill", label: "L Drill", section: "drills", prop: "lDrill" },
    ],
  },
];

const ALL_OPTIONS = OPTION_GROUPS.flatMap((group) => group.options);

/**
 * Selections from the GET's saved options, as { [key]: { showScore } }.
 * Options without data are dropped, so they start unchecked.
 */
export function selectionsFromSaved(savedOptions, availableOptions) {
  const available = new Set(availableOptions ?? []);
  return (savedOptions ?? []).reduce((selections, option) => {
    if (available.has(option?.key)) {
      selections[option.key] = { showScore: !!option.showScore };
    }
    return selections;
  }, {});
}

/**
 * The PUT body's options, in display order: [{ key, showScore }].
 */
export function selectionsToOptions(selections) {
  return ALL_OPTIONS.filter((option) => selections[option.key]).map(
    (option) => ({
      key: option.key,
      showScore: !!selections[option.key].showScore,
    }),
  );
}

/**
 * The card data the preview draws: the GET's cardData with only the checked
 * items, scores removed where Show Score is off, and profileLinkToken removed
 * when the QR code is off. The name is always kept.
 */
export function buildPreviewCard(cardData, selections, includeQrCode) {
  const card = {
    firstName: cardData?.firstName,
    lastName: cardData?.lastName,
  };

  ALL_OPTIONS.forEach((option) => {
    const selection = selections[option.key];
    if (!selection) return;

    if (option.fields) {
      option.fields.forEach((field) => {
        if (cardData?.[field] !== undefined) card[field] = cardData[field];
      });
      return;
    }

    const item = cardData?.[option.section]?.[option.prop];
    if (item === undefined) return;

    const { score, ...rest } = item;
    card[option.section] = {
      ...card[option.section],
      [option.prop]: selection.showScore ? item : rest,
    };
  });

  if (includeQrCode && cardData?.profileLinkToken) {
    card.profileLinkToken = cardData.profileLinkToken;
  }

  return card;
}
